'use client';

import { isMockModeClient } from '@/test/msw/config';
import { type PersonProperties, PostHogProvider } from '@monorepo/common';
import { Provider } from 'jotai';
import type { Session } from 'next-auth';
import { SessionProvider, useSession } from 'next-auth/react';
import { useEffect, useMemo, useState } from 'react';

// worker.start() の Promise はクライアントで一度だけ生成し、複数レンダーで共有する
let mockWorkerPromise: Promise<unknown> | null = null;

const startMockWorker = (): Promise<unknown> => {
  if (mockWorkerPromise) return mockWorkerPromise;
  mockWorkerPromise = (async () => {
    try {
      // 本番バンドルへ msw / worker を載せないため動的 import する
      const { worker } = await import('@/test/msw/browser');
      await worker.start({ onUnhandledRequest: 'bypass' });
    } catch (error) {
      // 起動失敗を握りつぶすと children が永久に描画されず原因が分かりづらいため、ログに残す。
      console.error('MSW worker の起動に失敗しました', error);
    }
  })();
  return mockWorkerPromise;
};

/**
 * モックモード時のみブラウザ MSW worker を起動する。
 *
 * worker 起動完了までクライアントの描画をブロックする。起動前にクライアント発の API fetch が
 * 走ると worker に横取りされず素通りするため（購入履歴→再購入のように遷移直後に操作する経路で
 * 競合する）、起動完了まで children を描画しない。
 * SSR と初回クライアントレンダーはともに未起動（null）で一致するため hydration 不整合は起きない。
 * `isMockModeClient()` は NEXT_PUBLIC_* のビルド時定数のため、本番では分岐ごとツリーシェイクされる。
 */
const MockWorkerGate = ({ children }: { children: React.ReactNode }) => {
  const [isWorkerReady, setIsWorkerReady] = useState(false);
  useEffect(() => {
    let isActive = true;
    startMockWorker().then(() => {
      if (isActive) setIsWorkerReady(true);
    });
    return () => {
      isActive = false;
    };
  }, []);
  if (!isWorkerReady) return null;
  return <>{children}</>;
};

export const MockProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  if (!isMockModeClient()) return <>{children}</>;
  return <MockWorkerGate>{children}</MockWorkerGate>;
};

export const JotaiProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => <Provider>{children}</Provider>;

/**
 * session を渡さないとブラウザが自力で /api/auth/session を取得し、失敗するとゲストで固定される（GF-2374）。
 * 渡せば初回レンダーから確定値になり、その取得自体が省略される。
 */
export const AuthProvider = ({
  children,
  session,
}: {
  children: React.ReactNode;
  session: Session | null;
}) => <SessionProvider session={session}>{children}</SessionProvider>;

/**
 * PostHog Provider をアプリ側の next-auth セッションに接続する。
 *
 * 各ログイン経路を個別に修正せず、セッションを単一の真実として顧客ID（distinctId）を渡し、
 * identify / reset の切り替えは共通 Provider 側に委譲する。
 * `useSession` を使うため AuthProvider（SessionProvider）の内側に配置すること。
 */
export const AnalyticsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { data: session } = useSession();
  // 顧客IDは数値型のため、PostHog の distinctId（文字列）に合わせて変換する。
  const distinctId = typeof session?.user?.id === 'number' ? String(session.user.id) : null;
  // gender / ageBand / linkedProviders を identify 時に person property として $set する。
  // null / undefined は載せない（未設定ユーザーで空値を送らない）。linkedProviders は連携追加/解除で
  // 変動するため、変化点では別途 setPersonProperties で即時 $set する（identify だけでは追随しない）。
  // 属性が1つも無ければ undefined を返し、identify に空 $set を渡さない。
  // 毎レンダーで新オブジェクトを作ると PostHogProvider の effect が無駄に再実行されるため memo 化する。
  const gender = session?.user?.gender;
  const ageBand = session?.user?.ageBand;
  const linkedProviders = session?.user?.linkedProviders;
  const personProperties = useMemo<PersonProperties | undefined>(() => {
    const properties: PersonProperties = {
      ...(gender ? { gender } : {}),
      ...(ageBand ? { ageBand } : {}),
      ...(linkedProviders ? { linkedProviders } : {}),
    };
    return Object.keys(properties).length > 0 ? properties : undefined;
  }, [gender, ageBand, linkedProviders]);
  return (
    <PostHogProvider distinctId={distinctId} personProperties={personProperties}>
      {children}
    </PostHogProvider>
  );
};
